import { Button } from '@repo/ui/components/button';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import {
  atFirstPage,
  type CursorTrail,
  describeCursorPage,
  pageBack,
  pageForward,
  pageNumber,
} from './cursor-pagination';

/**
 * The control under a **keyset**-paged list: where you are, and one step either
 * way.
 *
 * `Pager` beside this one is for the `limit`/`offset` routes and cannot serve
 * these: it draws nothing when `total <= limit`, and a keyset page has no total.
 * The two are separate components over separate sums for the reason
 * `cursor-pagination.ts` sets out — a single one that sometimes has a total
 * would be a type with a hole in it. Everything else about it is the same
 * shape, deliberately, so the two read alike on screen.
 *
 * ## Why "Next" is disabled rather than absent on the last page
 *
 * The route announces the end with `nextCursor: null` and there is no total to
 * predict it from, so the last page looks exactly like a full one until the
 * answer arrives. A Next button that vanished when the rows landed would move
 * Previous under a pointer already travelling towards it.
 *
 * ## No numbered pages, and here they are not even possible
 *
 * `Pager` leaves them out as chrome nobody uses. This one could not draw them:
 * a keyset route publishes no total, so there is no last page to number towards
 * and no way to jump to page four without walking through pages two and three.
 * The trail is what makes Previous work at all.
 *
 * ## Deliberately no retry loop
 *
 * Nothing here re-requests anything. `blossom-rest-api#347` has an out-of-range
 * cursor instant answering a **retryable** `500` rather than a coded refusal,
 * which is precisely what an automatic retry would spin on. Paging is a button a
 * person presses.
 */
export function CursorPager({
  trail,
  rows,
  nextCursor,
  onTrailChange,
  label,
  noun,
}: {
  readonly trail: CursorTrail;

  /** How many rows the current page actually holds. */
  readonly rows: number;

  /**
   * The cursor the current page answered with, sent back verbatim to reach the
   * next one. **`null` means this was the last page** — the only way to know,
   * since a keyset page publishes no total. Opaque: never constructed here.
   */
  readonly nextCursor: string | null;

  onTrailChange: (next: CursorTrail) => void;

  /**
   * What is being paged, for the navigation's accessible name — `"expenses"`.
   * Two paged lists on one screen are otherwise two landmarks called
   * "pagination".
   */
  readonly label: string;

  /** Singular, for the count line — `"expense"`. */
  readonly noun: string;
}) {
  const page = pageNumber(trail);

  // One page, and it is the whole list. The list's own empty or complete state
  // is already saying so, and a pair of disabled buttons under it is a second,
  // quieter voice on the same subject.
  if (page === 1 && nextCursor === null) return null;

  return (
    <nav aria-label={`${label} pages`} className="flex items-center justify-between gap-4 pt-2">
      {/* `polite`, so moving a page announces where you landed rather than
          interrupting. */}
      <p aria-live="polite" className="text-sm text-muted-foreground">
        {describeCursorPage({ page, rows, noun })}
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={atFirstPage(trail)}
          onClick={() => {
            onTrailChange(pageBack(trail));
          }}
        >
          <IconChevronLeft data-icon="inline-start" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={nextCursor === null}
          onClick={() => {
            onTrailChange(pageForward(trail, nextCursor));
          }}
        >
          Next
          <IconChevronRight data-icon="inline-end" />
        </Button>
      </div>
    </nav>
  );
}
